import React, {useEffect, useState} from 'react';
import {ChangeLogCheckIcon} from '../../../ui-components/Iconpack';
import {getImageSize} from '../../../utils/media';
import {CommonSpinLoader} from '../../../ui-components/loaders';
import {GetFileSize} from '../../../utils/helper';

export interface ChangeLogsProps {
    originalFileType: string;
    newFileType?: string;
    originalFileSize: number;
    cdnUrl: string;
    bgRemoved: boolean;
    bgReplaced: boolean;
    cropped: boolean;
    dimensionsChanged: boolean;
    fileQuality?: string;
    greyToneApplied: boolean;
    loading: boolean;
    className?: string;
}

const ChangeLog = (props: {children: React.ReactNode}) => {
    return (
        <div className={'mx-4 my-[6px] flex items-start justify-start rounded-[8px] bg-[#363F72] p-2'}>
            <div className={'mr-2 p-[3px]'}>
                <ChangeLogCheckIcon />
            </div>
            {props.children}
        </div>
    );
};

export const ChangeLogs: React.FC<ChangeLogsProps> = (props) => {
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const changeLogsExist = logs.length > 0 && !props.loading;

    async function prepareLogsUsingProps() {
        setLoading(true);
        const logs = [];
        const newImageSize = await getImageSize(props.cdnUrl);
        if (newImageSize > 0 && props.originalFileSize > 0 && newImageSize < props.originalFileSize) {
            const fileSizeLog = (
                <ChangeLog>
                    <div className={''}>
                        <span>
                            <span>File size shrunk from </span>
                            <span className={'text-red-500'}> {GetFileSize(props.originalFileSize)} </span>
                            to a lean
                            <span className={'text-green-500'}> {GetFileSize(newImageSize)} </span>
                        </span>
                    </div>
                </ChangeLog>
            );
            logs.push(fileSizeLog);
        }
        if (props.dimensionsChanged) {
            logs.push(
                <ChangeLog>
                    <span>Image dimensions have been changed</span>
                </ChangeLog>,
            );
        }
        if (props.cropped) {
            logs.push(
                <ChangeLog>
                    <span>Image cropped for a sharper focus</span>
                </ChangeLog>,
            );
        }
        if (props.bgRemoved) {
            logs.push(
                <ChangeLog>
                    <span>Background removed for a clean and focused image</span>
                </ChangeLog>,
            );
        }
        if (props.bgReplaced) {
            logs.push(
                <ChangeLog>
                    <span>Swapped the background to match your vision</span>
                </ChangeLog>,
            );
        }
        if (props.greyToneApplied) {
            logs.push(
                <ChangeLog>
                    <span>Greyscale applied: Striking black and white conversion achieved</span>
                </ChangeLog>,
            );
        }
        if (props.newFileType && props.newFileType.length > 0 && props.newFileType !== props.originalFileType) {
            logs.push(
                <ChangeLog>
                    <span>Image format has changed to {props.newFileType}</span>
                </ChangeLog>,
            );
        }
        setLogs(logs);
        setLoading(false);
    }

    useEffect(() => {
        prepareLogsUsingProps();
    }, [
        props.loading,
        props.cdnUrl,
        props.dimensionsChanged,
        props.newFileType,
        props.originalFileSize,
        props.cropped,
        props.bgReplaced,
        props.bgRemoved,
        props.originalFileType,
        props.newFileType,
        props.greyToneApplied,
    ]);

    return (
        <div>
            {changeLogsExist && (
                <div
                    className={`flex h-full max-w-[266px] flex-col bg-[#293056] text-[14px] text-white ${props.className || ''}`}>
                    <span className={'p-4 text-[18px] font-[700px]'}>Image Transformations</span>
                    {loading && (
                        <div className={'flex justify-center align-middle'}>
                            <CommonSpinLoader size={'xsm'} />
                        </div>
                    )}
                    {!loading && (
                        <>
                            {logs.map((log, index) => (
                                <div key={index}>{log}</div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
